import { Routes, Route, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { safeReturnTo } from './utils/safeReturnTo';
import { useI18n } from './contexts/I18nContext';
import { MainLayout } from './components/layout/MainLayout';
import { ProtectedRoute, SysAdminRoute, OrgAdminRoute, OrgOrEventAdminRoute, MemberRoute } from './components/layout/ProtectedRoute';
import { StadiumOperatorRoute } from './components/layout/StadiumOperatorRoute';
import { ScanStationLoading } from './pages/admin/ScanStation/components/ScanStationLoading';

// Lazy load pages for code splitting
import { lazy, Suspense } from 'react';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const LoginCallback = lazy(() => import('./pages/auth/LoginCallback'));
const BindingWizard = lazy(() => import('./pages/auth/BindingWizard'));
const AccessDenied = lazy(() => import('./pages/auth/AccessDenied'));

// Admin pages
const EventList = lazy(() => import('./pages/admin/EventList'));
const EventManage = lazy(() => import('./pages/admin/EventManage'));
const EventSetupWizard = lazy(() => import('./pages/admin/EventSetupWizard'));
const AccommodationWorkbench = lazy(() => import('./pages/admin/AccommodationWorkbench'));
const ParticipantMasterSheet = lazy(() => import('./pages/admin/ParticipantMasterSheet/ParticipantMasterSheet'));
const TransportationWorkbench = lazy(() => import('./pages/admin/TransportationWorkbenchV2'));
const TransportationPrint = lazy(() => import('./pages/admin/TransportationWorkbenchV2/TransportationPrint'));
const CheckinWorkstation = lazy(() => import('./pages/admin/CheckinWorkstation'));
const StationCardsPrint = lazy(() => import('./pages/admin/CheckinWorkstation/StationCardsPrint'));
const StadiumCheckin = lazy(() => import('./pages/admin/StadiumCheckin'));
const DoorCheckin = lazy(() => import('./pages/admin/DoorCheckin/DoorCheckin'));
const ScanStation = lazy(() => import('./pages/admin/ScanStation'));
const EventFormManage = lazy(() => import('./pages/admin/EventFormManage'));
const EventFormBuilder = lazy(() => import('./pages/admin/EventFormBuilder'));
const EventFormResponses = lazy(() => import('./pages/admin/EventFormResponses'));
const FeeManagement = lazy(() => import('./pages/admin/FeeManagement'));
const SimpleFeeManagement = lazy(() => import('./pages/admin/SimpleFeeManagement'));
const Reports = lazy(() => import('./pages/admin/Reports'));
const EventDashboard = lazy(() => import('./pages/admin/EventDashboard'));
const EventRolesReport = lazy(() => import('./pages/admin/EventRolesReport'));
const MemberDistributionReport = lazy(() => import('./pages/admin/MemberDistributionReport'));
const UserManagement = lazy(() => import('./pages/admin/UserManagement'));
const OrganizationManagement = lazy(() => import('./pages/admin/OrganizationManagement'));

// Public form page
const PublicFormPage = lazy(() => import('./pages/forms/PublicFormPage'));

// Public event self-registration (anonymous secret link)
const PublicEventRegister = lazy(() => import('./pages/public/PublicEventRegister'));
// /s/:token → a 報到點 link opens the check-in station, anything else the scan station.
const StationLinkRouter = lazy(() => import('./pages/public/PublicCheckinStation/StationLinkRouter'));

// Member pages - Event browsing and registration
const EventBrowse = lazy(() => import('./pages/events/EventBrowse'));
const EventDetailPublic = lazy(() => import('./pages/events/EventDetailPublic'));
const RegistrationFlow = lazy(() => import('./pages/registration/RegistrationFlow'));
const Confirmation = lazy(() => import('./pages/registration/Confirmation'));
const MyRegistrations = lazy(() => import('./pages/member/MyRegistrations'));
const RegistrationDetail = lazy(() => import('./pages/member/RegistrationDetail'));
const Journey = lazy(() => import('./pages/member/Journey'));
const TransportationForm = lazy(() => import('./pages/member/TransportationForm'));
const Settings = lazy(() => import('./pages/member/Settings'));
const Profile = lazy(() => import('./pages/member/Profile'));
const ServiceRun = lazy(() => import('./pages/member/ServiceRun'));

// OrgAdmin pages
const AssociationRegistrations = lazy(() => import('./pages/orgadmin/AssociationRegistrations'));
const AssociationMembers = lazy(() => import('./pages/orgadmin/AssociationMembers'));
const ProxyRegistration = lazy(() => import('./pages/orgadmin/ProxyRegistration'));
const BulkImport = lazy(() => import('./pages/orgadmin/BulkImport'));

function RedirectToWorkbench() {
  const { eventId } = useParams<{ eventId: string }>();
  return <Navigate to={`/admin/events/${eventId}/logistics/workbench`} replace />;
}

/** A bound user landing on /binding goes where they came from (a safe path), else the dashboard. */
export function BoundBindingRedirect() {
  const [searchParams] = useSearchParams();
  return <Navigate to={safeReturnTo(searchParams.get('returnTo')) ?? '/dashboard'} replace />;
}

function LoadingFallback() {
  const { t } = useI18n();
  return (
    <div className="loading-fallback">
      <div className="loading-brand">
        <img src="/blia_logo.png" alt="BLIA" className="loading-logo" />
        <h1 className="loading-title">{t('app.name')}</h1>
        <p className="loading-tagline">{t('app.tagline')}</p>
      </div>
      <div className="spinner" />
      <p className="loading-text">{t('common.loading')}</p>
    </div>
  );
}

function App() {
  const { isLoading, isAuthenticated, isBound } = useAuth();
  const { t } = useI18n();

  if (isLoading) {
    return (
      <div className="app-loading">
        <div className="loading-brand">
          <img src="/blia_logo.png" alt="BLIA" className="loading-logo" />
          <h1 className="loading-title">{t('app.name')}</h1>
          <p className="loading-tagline">{t('app.tagline')}</p>
        </div>
        <div className="spinner" />
        <p className="loading-text">{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="app">
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Landing page for unauthenticated users */}
          <Route path="/" element={
            isAuthenticated ? (
              isBound ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <Navigate to="/binding" replace />
              )
            ) : (
              <LandingPage />
            )
          } />

          {/* Redirect /login to landing */}
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="/login/callback" element={<LoginCallback />} />
          <Route path="/access-denied" element={<AccessDenied />} />

          {/* Binding route - requires auth but not binding */}
          <Route path="/binding" element={
            <ProtectedRoute>
              {isBound ? <BoundBindingRedirect /> : <BindingWizard />}
            </ProtectedRoute>
          } />

          {/* Public form page - accessible without authentication */}
          <Route path="/forms/:formId" element={<PublicFormPage />} />

          {/* Public event self-registration - anonymous secret link */}
          <Route path="/r/:token" element={<PublicEventRegister />} />

          {/* Public scan station — secret link + passcode, no account required */}
          <Route path="/s/:token" element={<StationLinkRouter />} />

          <Route path="/dashboard" element={
            <ProtectedRoute requireBound>
              <MainLayout>
                <Dashboard />
              </MainLayout>
            </ProtectedRoute>
          } />

          <Route path="/service/runs/:runId" element={
            <ProtectedRoute requireBound>
              <MainLayout>
                <ServiceRun />
              </MainLayout>
            </ProtectedRoute>
          } />

          {/* Admin routes - Event management accessible to OrgAdmin, SysAdmin, and event admins */}
          <Route path="/admin/events" element={
            <OrgOrEventAdminRoute>
              <MainLayout>
                <EventList />
              </MainLayout>
            </OrgOrEventAdminRoute>
          } />

          {/* Redirect old create path to wizard */}
          <Route path="/admin/events/create" element={
            <Navigate to="/admin/events/wizard" replace />
          } />

          {/* User Management */}
          <Route path="/admin/users" element={
            <SysAdminRoute>
              <MainLayout>
                <UserManagement />
              </MainLayout>
            </SysAdminRoute>
          } />

          {/* Organization Management */}
          <Route path="/admin/organizations" element={
            <SysAdminRoute>
              <MainLayout>
                <OrganizationManagement />
              </MainLayout>
            </SysAdminRoute>
          } />

          {/* New Unified Event Setup Wizard. No phone bottom nav: it covered
              the wizard's own fixed 取消 / 下一步 bar. */}
          <Route path="/admin/events/wizard" element={
            <OrgAdminRoute>
              <MainLayout hideMobileNav>
                <EventSetupWizard />
              </MainLayout>
            </OrgAdminRoute>
          } />

          {/* Event Edit - Reuses EventSetupWizard (same fixed bar: 儲存變更) */}
          <Route path="/admin/events/:eventId/edit" element={
            <OrgAdminRoute>
              <MainLayout hideMobileNav>
                <EventSetupWizard />
              </MainLayout>
            </OrgAdminRoute>
          } />

          {/* Event Statistics Dashboard */}
          <Route path="/admin/events/:eventId/dashboard" element={
            <OrgOrEventAdminRoute>
              <MainLayout>
                <EventDashboard />
              </MainLayout>
            </OrgOrEventAdminRoute>
          } />

          {/* Fee Management Page */}
          <Route path="/admin/events/:eventId/fee-management" element={
            <OrgOrEventAdminRoute>
              <MainLayout>
                <FeeManagement />
              </MainLayout>
            </OrgOrEventAdminRoute>
          } />

          {/* Simple Fee Management Page */}
          <Route path="/admin/events/:eventId/simple-fee-management" element={
            <OrgOrEventAdminRoute>
              <MainLayout>
                <SimpleFeeManagement />
              </MainLayout>
            </OrgOrEventAdminRoute>
          } />

          {/* Event Management Page - Main event management page with collapsible details */}
          <Route path="/admin/events/:eventId/manage" element={
            <OrgOrEventAdminRoute>
              <MainLayout>
                <EventManage />
              </MainLayout>
            </OrgOrEventAdminRoute>
          } />

          {/* Event Detail Page - Also uses new EventManage */}
          <Route path="/admin/events/:eventId" element={
            <OrgOrEventAdminRoute>
              <MainLayout>
                <EventManage />
              </MainLayout>
            </OrgOrEventAdminRoute>
          } />

          {/* Legacy logistics/accommodation route — redirect to unified workbench (Setup tab). */}
          <Route
            path="/admin/events/:eventId/logistics/accommodation"
            element={<RedirectToWorkbench />}
          />

          {/* Accommodation Workbench — Setup + Assign + Timeline in one place */}
          <Route path="/admin/events/:eventId/logistics/workbench" element={
            <OrgAdminRoute>
              <AccommodationWorkbench />
            </OrgAdminRoute>
          } />

          {/* 參加者總表 — one row per participant: registration, stay, transport, check-in */}
          <Route path="/admin/events/:eventId/master-sheet" element={
            <OrgAdminRoute>
              <ParticipantMasterSheet />
            </OrgAdminRoute>
          } />

          {/* Transportation Workbench - Multi-leg (001-transport-multi-leg-workbench) */}
          <Route path="/admin/events/:eventId/transportation" element={
            <OrgAdminRoute>
              <TransportationWorkbench />
            </OrgAdminRoute>
          } />
          <Route path="/admin/events/:eventId/transportation/print" element={
            <OrgAdminRoute>
              <TransportationPrint />
            </OrgAdminRoute>
          } />

          {/* Check-in Workstation — Arrival Day Operations Console (005-checkin-workstation) */}
          <Route path="/admin/events/:eventId/checkin" element={
            <OrgAdminRoute>
              <CheckinWorkstation />
            </OrgAdminRoute>
          } />
          {/* Printable 報到點 QR cards (A4, four per page) */}
          <Route path="/admin/events/:eventId/checkin/cards" element={
            <OrgAdminRoute>
              <StationCardsPrint />
            </OrgAdminRoute>
          } />

          {/* Stadium Check-in — Simple-event seating + scan workbench (006-stadium-checkin) */}
          <Route path="/admin/events/:eventId/stadium-checkin" element={
            <OrgOrEventAdminRoute>
              <StadiumCheckin />
            </OrgOrEventAdminRoute>
          } />

          {/* Door check-in — 一般活動 organizer door screen + volunteer link */}
          <Route path="/admin/events/:eventId/door-checkin" element={
            <OrgOrEventAdminRoute>
              <DoorCheckin />
            </OrgOrEventAdminRoute>
          } />

          {/* Scan Station — kiosk-style continuous scan page for operators.
              Local Suspense + dark fallback so the lazy chunk load uses the
              same loading screen as the access probe and the data fetch
              (no white-then-black flash on first navigation). */}
          <Route path="/admin/events/:eventId/scan-station" element={
            <StadiumOperatorRoute>
              <Suspense fallback={<ScanStationLoading />}>
                <ScanStation />
              </Suspense>
            </StadiumOperatorRoute>
          } />

          {/* Form Management Routes */}
          <Route path="/admin/events/:eventId/forms" element={
            <OrgAdminRoute>
              <MainLayout>
                <EventFormManage />
              </MainLayout>
            </OrgAdminRoute>
          } />

          <Route path="/admin/events/:eventId/forms/new" element={
            <OrgAdminRoute>
              <MainLayout>
                <EventFormBuilder />
              </MainLayout>
            </OrgAdminRoute>
          } />

          <Route path="/admin/events/:eventId/forms/:formId/edit" element={
            <OrgAdminRoute>
              <MainLayout>
                <EventFormBuilder />
              </MainLayout>
            </OrgAdminRoute>
          } />

          <Route path="/admin/events/:eventId/forms/:formId/responses" element={
            <OrgAdminRoute>
              <MainLayout>
                <EventFormResponses />
              </MainLayout>
            </OrgAdminRoute>
          } />

          {/* Reports - SysAdmin only (with whitelist) */}
          <Route path="/admin/reports" element={
            <OrgAdminRoute>
              <MainLayout>
                <Reports />
              </MainLayout>
            </OrgAdminRoute>
          } />

          <Route path="/admin/reports/event-roles" element={
            <OrgAdminRoute>
              <MainLayout>
                <EventRolesReport />
              </MainLayout>
            </OrgAdminRoute>
          } />

          <Route path="/admin/reports/member-distribution" element={
            <OrgAdminRoute>
              <MainLayout>
                <MemberDistributionReport />
              </MainLayout>
            </OrgAdminRoute>
          } />

          {/* Member routes - Event browsing and registration */}
          <Route path="/events" element={
            <ProtectedRoute requireBound>
              <MainLayout>
                <EventBrowse />
              </MainLayout>
            </ProtectedRoute>
          } />

          <Route path="/events/:eventId" element={
            <ProtectedRoute requireBound>
              <MainLayout>
                <EventDetailPublic />
              </MainLayout>
            </ProtectedRoute>
          } />

          <Route path="/events/:eventId/register" element={
            <ProtectedRoute requireBound>
              <MainLayout>
                <RegistrationFlow />
              </MainLayout>
            </ProtectedRoute>
          } />

          <Route path="/events/:eventId/register/confirmation" element={
            <ProtectedRoute requireBound>
              <MainLayout>
                <Confirmation />
              </MainLayout>
            </ProtectedRoute>
          } />

          <Route path="/registrations" element={
            <ProtectedRoute requireBound>
              <MainLayout>
                <MyRegistrations />
              </MainLayout>
            </ProtectedRoute>
          } />

          <Route path="/registrations/:registrationId" element={
            <ProtectedRoute requireBound>
              <MainLayout>
                <RegistrationDetail />
              </MainLayout>
            </ProtectedRoute>
          } />

          {/* 我的行程 - Member's day-by-day journey (published logistics + own travel) */}
          <Route path="/registrations/:registrationId/journey" element={
            <ProtectedRoute requireBound>
              <MainLayout>
                <Journey />
              </MainLayout>
            </ProtectedRoute>
          } />

          {/* Transportation Form - Member fills out travel details */}
          <Route path="/registrations/:registrationId/transportation" element={
            <ProtectedRoute requireBound>
              <MainLayout>
                <TransportationForm />
              </MainLayout>
            </ProtectedRoute>
          } />

          {/* Settings - Accessible to all authenticated users */}
          <Route path="/settings" element={
            <ProtectedRoute>
              <MainLayout>
                <Settings />
              </MainLayout>
            </ProtectedRoute>
          } />

          {/* Profile - Member profile page */}
          <Route path="/profile" element={
            <ProtectedRoute requireBound>
              <MainLayout>
                <Profile />
              </MainLayout>
            </ProtectedRoute>
          } />

          {/* OrgAdmin routes */}
          <Route path="/orgadmin/registrations" element={
            <OrgOrEventAdminRoute>
              <MainLayout>
                <AssociationRegistrations />
              </MainLayout>
            </OrgOrEventAdminRoute>
          } />

          <Route path="/orgadmin/events/:eventId/members" element={
            <OrgAdminRoute>
              <MainLayout>
                <AssociationMembers />
              </MainLayout>
            </OrgAdminRoute>
          } />

          <Route path="/orgadmin/events/:eventId/proxy-register" element={
            <OrgAdminRoute>
              <MainLayout>
                <ProxyRegistration />
              </MainLayout>
            </OrgAdminRoute>
          } />

          <Route path="/orgadmin/events/:eventId/bulk-import" element={
            <OrgAdminRoute>
              <MainLayout>
                <BulkImport />
              </MainLayout>
            </OrgAdminRoute>
          } />

          {/* Catch-all redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </div>
  );
}

export default App;
